import React, { useState, useCallback, useRef, useMemo } from 'react'
import { Button, Input } from 'antd'
import { SwapOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons'
import scrollIntoView from 'scroll-into-view'
import QuantitiesDynamicCell from './TableComponents/QuantitiesDynamicCell'
import {
  calculateColumnWidthBasedOnText,
  renderTranslation,
  retrieveSelloutAvarageWeighted,
  setStickyTableHead,
  stopPropagation,
  sendMessage,
} from '../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { setCapitalLetter, unsensitiveCaseSearchMatch } from '../../Helpers/StringHelpers'
import { saveFilters, clearTagFilters } from '../../Slices/Kiosk/TableOptionsSlice'
import {
  Total,
  ExistingStock,
  AvailableStock,
  StoreName,
  PlaceHolderCol,
  ExtraRecapRow,
  Sellout,
  QuantitiesLabelLangClass,
  QuantitiesHeadLangClass,
  ConfigurationForm,
  Stores,
  ReturnCustomer,
} from '../../Constants/Configuration'
import { ArticleColumn } from '../../Constants/TableConfiguration'
import { populateTableWithArticles, retrieveArticleDetailData } from '../../Services/Kiosk/ArticleServices'
import { resetHandledQuantitySelection } from '../../Slices/Kiosk/QuantitiesSlice'
import {
  checkIfStoreHasStockOrAvailability,
  retrieveActiveStoreKeysSortByCurrentFilter,
  retrieveSortedSizes,
  retrieveVariantsBasedOnFilter,
} from '../../Helpers/QuantitiesHelper'
import { retrieveSelectedArticleKey } from '../../Helpers/ArticlesHelper'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { dispatchablePrepareStores, filterStores } from 'Helpers/StoreHelper'

function Quantities() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const quantities = useAppSelector(state => state.present.articleDetail.articleSelected.quantities)
  const quantitiesColumns = useAppSelector(state => state.present.quantities.value.columns)
  const quantitiesFilters = useAppSelector(state => state.present.quantities.value.filters)
  const enableAllVariant = useAppSelector(state => state.present.quantities.value.enableAllVariant)
  const unlockHandling = useAppSelector(state => state.present.quantities.value.unlockHandling)
  const handledQuantitySelection = useAppSelector(state => state.present.quantities.value.handledQuantitySelection)
  const situationFilter = useAppSelector(state => state.present.quantities.value.situationFilter)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const currentConfiguration = useAppSelector(state => state.present.configuration.currentConfiguration)
  const activeStore = useAppSelector(state => state.present.configuration.activeStore)
  const formConfigurationValue = useAppSelector(state => state.present.configuration.formConfigurationValue)
  const serverConfig = useAppSelector(state => state.present.article.serverConfig)
  const userValue = useAppSelector(state => state.present.configuration.userValue)
  const publicWorkspace = useAppSelector(state => state.present.configuration.publicWorkspace)

  const currentStoreOption = useMemo(() => dispatch(dispatchablePrepareStores()), [activeStore])

  const isConfigurationFormGruccia = formConfigurationValue === ConfigurationForm.GRUCCIA

  const [searchValue, setSearchValue] = useState('')
  const [isHeadSticky, setIsHeadSticky] = useState(false)

  const tableRef = useRef(null)

  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

  window.onscroll = useCallback(() => {
    setStickyTableHead('.quantities-table-head', tableRef, isHeadSticky, setIsHeadSticky)
  }, [isHeadSticky])

  window.onresize = useCallback(() => {
    setStickyTableHead('.quantities-table-head', tableRef, isHeadSticky, setIsHeadSticky)
  }, [isHeadSticky])

  const quantitiesVariant = useMemo(() => {
    return retrieveVariantsBasedOnFilter(enableAllVariant, quantities)
  }, [enableAllVariant, quantities])

  const sizesAvailable = useMemo(() => {
    return retrieveSortedSizes(quantitiesVariant, unlockHandling)
  }, [quantitiesVariant, unlockHandling])

  const columnsWithSizes = useMemo(() => {
    const columns = [...quantitiesColumns]
    const sizeColumns = sizesAvailable.map(size => {
      return { key: size.sizeId, title: size.name, isSize: true }
    })
    return columns.concat(sizeColumns)
  }, [quantitiesColumns, sizesAvailable])

  const sendQuantitySelection = useCallback(
    storesLength => {
      if (
        (currentStoreOption.length === 0 && activeStore === Stores.Local) ||
        (storesLength === 0 && activeStore !== Stores.Local)
      ) {
        return
      }

      //total column removed because is needed only for render
      const quantityValues = handledQuantitySelection.filter(x => x.columnKey !== Total)

      sendMessage({ values: quantityValues, type: 'Movimenti' })

      dispatch(resetHandledQuantitySelection())
      retrieveArticleDetailData(articleKey, dispatch)
    },
    [activeStore, articleKey, currentStoreOption.length, dispatch, handledQuantitySelection],
  )

  const updateArticlesBasedOnTableModel = useCallback(
    modelName => {
      const articleTable = document.getElementById('article-table')

      scrollIntoView(articleTable, {
        align: {
          top: 0,
          lockX: true,
        },
        isScrollable: (target, defaultIsScrollable) => {
          return defaultIsScrollable(target)
        },
      })

      setTimeout(() => {
        dispatch(
          clearTagFilters({
            configurationName: currentConfiguration,
            user: userValue,
            serverConfig: serverConfig,
            publicWorkspace: publicWorkspace,
          }),
        )
        dispatch(saveFilters({ key: ArticleColumn.ProductCode, value: modelName }))

        dispatch(populateTableWithArticles())
      }, 500)
    },
    [dispatch, currentConfiguration, userValue, serverConfig],
  )

  const filterStoresBySearchedValue = useCallback(
    stores => {
      if (!searchValue || searchValue.length < 2) return stores

      return stores.filter(store => {
        return unsensitiveCaseSearchMatch(store.storeName, searchValue)
      })
    },
    [searchValue],
  )

  const renderSizeCells = useCallback(
    (store, variant, storeKey, baseKey, currentStoreRowSizesValues, cssClass) => {
      return sizesAvailable.map(size => {
        const keyValue = size.sizeId
        return (
          <td key={baseKey + keyValue} className={cssClass}>
            <QuantitiesDynamicCell
              storeId={store.storeId}
              storeActiveSizes={currentStoreRowSizesValues}
              rowKey={storeKey}
              columnKey={size.name}
              sizeId={size.sizeId}
              variantId={variant.variantId}
            />
          </td>
        )
      })
    },
    [sizesAvailable],
  )

  const renderSituationRow = useCallback(
    (
      reactKey,
      storeKey,
      currentStatus,
      variant,
      storesTotalValue,
      storeKeyRowValues,
      activeStoreKeys,
      storesFiltered,
    ) => {
      const existingStockRowSpan =
        activeStoreKeys.indexOf(AvailableStock) !== -1
          ? unlockHandling
            ? !enableAllVariant
              ? 3
              : 2
            : 2
          : unlockHandling
          ? enableAllVariant
            ? 2
            : 1
          : 1
      return (
        <tr
          key={'row-' + reactKey}
          className={
            (storeKey === ExistingStock ? 'existing-stock-row' : '') +
            (storeKey === AvailableStock ? 'availability-row' : '')
          }
        >
          {
            //first cell
            storeKey === ExistingStock ? (
              <td rowSpan={existingStockRowSpan}></td>
            ) : storeKey === AvailableStock ? (
              activeStoreKeys.indexOf(storeKey) !== -1 ? (
                <td className=' bold'>{renderTranslation(language, TranslationKeys.acronymAvailability)}</td>
              ) : null
            ) : storeKey !== AvailableStock && storeKey !== ExistingStock ? (
              <td colSpan={2} className={storeKey.toLowerCase() + '-cell filter-name-cell'}>
                <span className={'quantities-type-box pretext-block ' + currentStatus}></span>
                {renderTranslation(language, TranslationKeys[QuantitiesLabelLangClass + setCapitalLetter(storeKey)])}
              </td>
            ) : null
          }
          {
            //second cell only for existingstock
            storeKey === ExistingStock ? (
              activeStoreKeys.indexOf(storeKey) !== -1 ? (
                <td className='stock-cell bold'>{renderTranslation(language, TranslationKeys.acronymExistingStock)}</td>
              ) : null
            ) : null
          }
          {
            //total cell
            <td>
              <QuantitiesDynamicCell
                storeId={''}
                storeActiveSizes={[]}
                rowKey={storeKey}
                columnKey={Total}
                sizeId={0}
                quantity={storesTotalValue}
                variantId={variant.variantId}
              />
            </td>
          }
          {sizesAvailable.map(size => {
            //sizes cell
            let storesSizeValue =
              storeKey === Sellout
                ? retrieveSelloutAvarageWeighted(storesFiltered, size.sizeId)
                : storeKeyRowValues
                    .filter(x => x.sizeId === size.sizeId)
                    .map(x => x.quantity)
                    .reduce((prev, curr) => curr + prev, null)

            const keyValue = size.sizeId
            return (
              <td key={reactKey + keyValue}>
                <QuantitiesDynamicCell
                  storeId={''}
                  storeActiveSizes={[]}
                  rowKey={storeKey}
                  columnKey={size.name}
                  sizeId={size.sizeId}
                  quantity={storesSizeValue}
                  variantId={variant.variantId}
                />
              </td>
            )
          })}
        </tr>
      )
    },
    [enableAllVariant, language, sizesAvailable, unlockHandling],
  )

  const renderNoStoreRows = useCallback(
    (storeKeyRows, variant) => {
      //Render when there are no stores
      const stockCellCssClass = 'stock-cell bold'
      const rowSpan = unlockHandling ? (!enableAllVariant ? 2 : 1) : 1

      storeKeyRows.push(
        <tr key='empy-existing-row'>
          <td rowSpan={rowSpan}>{renderTranslation(language, TranslationKeys.tableNoResults)}</td>
          <td className={stockCellCssClass}>{renderTranslation(language, TranslationKeys.acronymExistingStock)}</td>
          {
            <td className={''}>
              <QuantitiesDynamicCell
                storeId={''}
                storeActiveSizes={[]}
                rowKey={ExistingStock}
                columnKey={Total}
                sizeId={0}
                variantId={0}
                allValueSelected={false}
              />
            </td>
          }
          {sizesAvailable.map(size => {
            const keyValue = size.sizeId
            return (
              <td key={'size-' + keyValue}>
                <QuantitiesDynamicCell
                  storeId={''}
                  storeActiveSizes={[]}
                  rowKey={ExistingStock}
                  columnKey={size.name}
                  sizeId={size.sizeId}
                  variantId={variant.variantId}
                />
              </td>
            )
          })}
        </tr>,
      )
    },
    [enableAllVariant, language, sizesAvailable, unlockHandling],
  )

  const renderSituationTableBoby = useCallback(
    (stores, storeKeyRows, variant) => {
      if (stores && stores.length > 0) {
        const storesFiltered = filterStores(activeStore, currentStoreOption, stores)

        const activeStoreKeys =
          storesFiltered?.length > 0 ? Object.keys(storesFiltered[0]).filter(x => x !== StoreName) : 0
        const currentFilters = quantitiesFilters.map(filter => {
          return filter.active ? filter.value : null
        })

        const activeStoreKeysFiltered = retrieveActiveStoreKeysSortByCurrentFilter(activeStoreKeys, currentFilters)

        activeStoreKeysFiltered.forEach(storeKey => {
          const storeKeyRowValues = []
          const keyToSet = 'row-' + storeKey

          storesFiltered.forEach(store => {
            store[storeKey].forEach(colElement => {
              storeKeyRowValues.push(colElement)
            })
          })

          const storesTotalValue =
            storeKey === Sellout
              ? retrieveSelloutAvarageWeighted(storesFiltered, null)
              : storeKeyRowValues
                  .filter(x => x.name === Total)
                  .map(x => x.quantity)
                  .reduce((prev, curr) => curr + prev, null)
          const currentFilterInfo = quantitiesFilters.filter(element => element.value === storeKey)
          const currentStatus = currentFilterInfo.length > 0 ? currentFilterInfo[0].status : ''

          if (storeKey === ExistingStock || storeKey === AvailableStock || (storesTotalValue && storesTotalValue > 0)) {
            storeKeyRows.push(
              renderSituationRow(
                keyToSet,
                storeKey,
                currentStatus,
                variant,
                storesTotalValue,
                storeKeyRowValues,
                activeStoreKeysFiltered,
                storesFiltered,
              ),
            )
          }
        })
      }
    },
    [currentStoreOption, quantitiesFilters, renderSituationRow],
  )

  const tableBodyRows = useMemo(() => {
    if (quantitiesVariant.length > 0) {
      return quantitiesVariant.map(variant => {
        const stores = variant.stores
        const storeKeyRows = []
        if (!enableAllVariant && situationFilter && !unlockHandling) {
          //render situation rows
          renderSituationTableBoby(stores, storeKeyRows, variant)
        } else {
          const storesFiltered = filterStoresBySearchedValue(filterStores(activeStore, currentStoreOption, stores))
          const storesLength = stores ? stores.length : 0
          if (enableAllVariant) {
            //variant name and model row
            storeKeyRows.push(
              <tr key={variant.modelName + '-' + variant.variantName + '-' + variant.variantColorDesc}>
                <td className='variant-name-row' colSpan={columnsWithSizes.length}>
                  <span
                    className='variant-name'
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      updateArticlesBasedOnTableModel(variant.modelName)
                    }}
                  >
                    {variant.modelName}
                  </span>
                  <span className='model'>{variant.variantName}</span>
                  <span className='color'>{variant.variantColorDesc}</span>
                </td>
              </tr>,
            )
          }
          if (storesFiltered.length > 0) {
            storesFiltered.forEach((store, storeIndex) => {
              const activeStoreKeys = Object.keys(store)
              const currentFilters = quantitiesFilters.map(filter => {
                if (!enableAllVariant) {
                  return filter.active ? filter.value : null
                } else {
                  return filter.active && (filter.value === ExistingStock || filter.value === AvailableStock)
                    ? filter.value
                    : null
                }
              })

              const hasStoreStockOrAvilabilityValue = checkIfStoreHasStockOrAvailability(store)

              const activeStoreKeysFiltered = retrieveActiveStoreKeysSortByCurrentFilter(
                activeStoreKeys,
                currentFilters,
              )
              activeStoreKeysFiltered.forEach(storeKey => {
                const keyToSet = store.storeId + '-' + storeKey + '-' //keys to prevent react warning
                const currentStoreRowSizesValues = store[storeKey]

                if (storeKey === ExistingStock) {
                  //first Row
                  //check if all values match to set total column active
                  const isAvailableRowValuesSelected =
                    currentStoreRowSizesValues.filter(element => element.name !== Total).length ===
                    handledQuantitySelection.filter(
                      element =>
                        element.storeId === store.storeId &&
                        element.rowKey === storeKey &&
                        element.columnKey !== Total &&
                        currentStoreRowSizesValues.find(
                          activeSize =>
                            element.sizeId === activeSize.sizeId &&
                            element.quantity ===
                              (activeSize.quantity > 0 ? activeSize.quantity : Math.abs(activeSize.quantity)),
                        ),
                    ).length
                  const stockCellCssClass = 'stock-cell bold'
                  const existingStockCellClass = 'existing-stock-cell'
                  const hideExistingStockRowClass = unlockHandling ? 'd-none' : ''
                  const isAvailableStockRowVisibile = activeStoreKeysFiltered.indexOf(AvailableStock) !== -1

                  if (!enableAllVariant) {
                    const rowSpan = isAvailableStockRowVisibile ? (unlockHandling ? 3 : 2) : unlockHandling ? 2 : 1

                    storeKeyRows.push(
                      <tr key={'row-' + keyToSet} className='existing-stock-row'>
                        <td rowSpan={rowSpan} className=' bold'>
                          {store.storeName}
                        </td>
                        {activeStoreKeysFiltered.indexOf(storeKey) !== -1 ? (
                          <>
                            <td
                              className={
                                existingStockCellClass + ' ' + stockCellCssClass + ' ' + hideExistingStockRowClass
                              }
                            >
                              {renderTranslation(language, TranslationKeys.acronymExistingStock)}
                            </td>
                            {
                              <td className={existingStockCellClass + ' ' + hideExistingStockRowClass}>
                                <QuantitiesDynamicCell
                                  storeId={store.storeId}
                                  storeActiveSizes={currentStoreRowSizesValues}
                                  rowKey={storeKey}
                                  columnKey={Total}
                                  sizeId={0}
                                  variantId={variant.variantId}
                                  allValueSelected={isAvailableRowValuesSelected}
                                />
                              </td>
                            }
                            {renderSizeCells(
                              store,
                              variant,
                              storeKey,
                              keyToSet,
                              currentStoreRowSizesValues,
                              existingStockCellClass + ' ' + hideExistingStockRowClass,
                            )}
                          </>
                        ) : null}
                      </tr>,
                    )
                  } else {
                    const availableStockSizeValue = store[AvailableStock]
                    const totalColumnValues = currentStoreRowSizesValues.find(x => x.name === Total)
                    const availableStockTotalColumnValues = availableStockSizeValue.find(x => x.name === Total)

                    const rowSpan =
                      isAvailableStockRowVisibile &&
                      availableStockTotalColumnValues &&
                      availableStockTotalColumnValues.quantity > 0
                        ? 2
                        : 1

                    if (totalColumnValues && totalColumnValues.quantity > 0) {
                      storeKeyRows.push(
                        <tr key={'row-' + keyToSet} className='existing-stock-row'>
                          <td rowSpan={rowSpan}>{store.storeName}</td>
                          {activeStoreKeysFiltered.indexOf(storeKey) !== -1 ? (
                            <>
                              <td
                                className={
                                  existingStockCellClass + ' ' + stockCellCssClass + ' ' + hideExistingStockRowClass
                                }
                              >
                                {renderTranslation(language, TranslationKeys.acronymExistingStock)}
                              </td>
                              {
                                <td className={existingStockCellClass + ' ' + hideExistingStockRowClass}>
                                  <QuantitiesDynamicCell
                                    storeId={store.storeId}
                                    storeActiveSizes={currentStoreRowSizesValues}
                                    rowKey={storeKey}
                                    columnKey={Total}
                                    sizeId={0}
                                    variantId={variant.variantId}
                                    allValueSelected={isAvailableRowValuesSelected}
                                  />
                                </td>
                              }
                              {renderSizeCells(
                                store,
                                variant,
                                storeKey,
                                keyToSet,
                                currentStoreRowSizesValues,
                                existingStockCellClass + ' ' + hideExistingStockRowClass,
                              )}
                            </>
                          ) : null}
                        </tr>,
                      )
                    }
                  }
                }

                if (storeKey === AvailableStock) {
                  const currentFilter = quantitiesFilters.find(filter => filter.value === storeKey)
                  const isCurrentFilterVisible = currentFilter ? currentFilter.visible : ''
                  const isAvailableStockRowVisibile = activeStoreKeysFiltered.indexOf(storeKey) !== -1

                  if (isCurrentFilterVisible) {
                    //check if all values match to set total column active
                    const isAvailableRowValuesSelected =
                      currentStoreRowSizesValues.filter(element => element.name !== Total).length ===
                      handledQuantitySelection.filter(
                        element =>
                          element.storeId === store.storeId &&
                          element.rowKey === storeKey &&
                          element.columnKey !== Total &&
                          currentStoreRowSizesValues.find(
                            activeSize =>
                              element.sizeId === activeSize.sizeId &&
                              element.quantity ===
                                (activeSize.quantity > 0 ? activeSize.quantity : Math.abs(activeSize.quantity)),
                          ),
                      ).length
                    const availableCellCssClass = unlockHandling ? 'cursor-pointer bold selected' : 'bold'
                    const availabilityCell = 'availability-cell'

                    if (!enableAllVariant) {
                      if (isAvailableStockRowVisibile) {
                        storeKeyRows.push(
                          <tr key={'row-' + keyToSet} className='availability-row'>
                            <td className={availableCellCssClass}>
                              {renderTranslation(language, TranslationKeys.acronymAvailability)}
                            </td>
                            {
                              <td className={availabilityCell}>
                                <QuantitiesDynamicCell
                                  storeId={store.storeId}
                                  storeActiveSizes={currentStoreRowSizesValues}
                                  rowKey={storeKey}
                                  columnKey={Total}
                                  sizeId={0}
                                  variantId={variant.variantId}
                                  allValueSelected={isAvailableRowValuesSelected}
                                />
                              </td>
                            }
                            {renderSizeCells(
                              store,
                              variant,
                              storeKey,
                              keyToSet,
                              currentStoreRowSizesValues,
                              availabilityCell,
                            )}
                          </tr>,
                        )
                      }
                    } else {
                      const existingStockSizeValue = store[ExistingStock]
                      const totalColumnValues = currentStoreRowSizesValues.find(x => x.name === Total)
                      const existingStockTotalColumnValues = existingStockSizeValue.find(x => x.name === Total)

                      if (existingStockTotalColumnValues && existingStockTotalColumnValues.quantity > 0) {
                        if (totalColumnValues && totalColumnValues.quantity > 0) {
                          storeKeyRows.push(
                            <tr key={'row-' + keyToSet} className='availability-row'>
                              <td className={availableCellCssClass}>
                                {renderTranslation(language, TranslationKeys.acronymAvailability)}
                              </td>
                              {
                                <td className={availabilityCell}>
                                  <QuantitiesDynamicCell
                                    storeId={store.storeId}
                                    storeActiveSizes={currentStoreRowSizesValues}
                                    rowKey={storeKey}
                                    columnKey={Total}
                                    sizeId={0}
                                    variantId={variant.variantId}
                                    allValueSelected={isAvailableRowValuesSelected}
                                  />
                                </td>
                              }
                              {renderSizeCells(
                                store,
                                variant,
                                storeKey,
                                keyToSet,
                                currentStoreRowSizesValues,
                                availabilityCell,
                              )}
                            </tr>,
                          )
                        }
                      } else {
                        if (totalColumnValues && totalColumnValues.quantity > 0) {
                          storeKeyRows.push(
                            <tr key={'row-' + keyToSet} className='availability-row'>
                              <td rowSpan={1}>{store.storeName}</td>
                              {activeStoreKeysFiltered.indexOf(storeKey) !== -1 ? (
                                <>
                                  <td className={availabilityCell}>
                                    {renderTranslation(language, TranslationKeys.acronymAvailability)}
                                  </td>
                                  {
                                    <td className={availabilityCell}>
                                      <QuantitiesDynamicCell
                                        storeId={store.storeId}
                                        storeActiveSizes={currentStoreRowSizesValues}
                                        rowKey={storeKey}
                                        columnKey={Total}
                                        sizeId={0}
                                        variantId={variant.variantId}
                                        allValueSelected={isAvailableRowValuesSelected}
                                      />
                                    </td>
                                  }
                                  {renderSizeCells(
                                    store,
                                    variant,
                                    storeKey,
                                    keyToSet,
                                    currentStoreRowSizesValues,
                                    availabilityCell,
                                  )}
                                </>
                              ) : null}
                            </tr>,
                          )
                        }
                      }
                    }

                    //Movementation view total row and button if available stock is in filter
                    if (unlockHandling && !enableAllVariant) {
                      storeKeyRows.push(
                        <tr key={'total-row-' + keyToSet}>
                          <td className='total-cell'></td>
                          {
                            <td>
                              <QuantitiesDynamicCell
                                storeId={store.storeId}
                                storeActiveSizes={currentStoreRowSizesValues}
                                rowKey={ExtraRecapRow}
                                columnKey={Total}
                                sizeId={0}
                                variantId={variant.variantId}
                              />
                            </td>
                          }
                          {renderSizeCells(
                            store,
                            variant,
                            ExtraRecapRow,
                            'total-' + keyToSet,
                            currentStoreRowSizesValues,
                            '',
                          )}
                        </tr>,
                      )

                      const hasHandling =
                        handledQuantitySelection.filter(element => element.storeId === store.storeId).length !== 0
                      if (hasHandling) {
                        storeKeyRows.push(
                          <tr key={'button-row-' + keyToSet}>
                            <td colSpan={columnsWithSizes.length} className='quantities-button-row'>
                              <Button
                                className={
                                  'btn standard-btn' +
                                  (handledQuantitySelection.filter(element => element.storeId === store.storeId)
                                    .length !== 0 && unlockHandling
                                    ? ''
                                    : ' d-none')
                                }
                                onClick={() => {
                                  sendQuantitySelection(storesLength)
                                }}
                                size='large'
                                block
                              >
                                <SwapOutlined />{' '}
                                {renderTranslation(language, TranslationKeys.quantitiesHandledButtonLabel)}
                              </Button>
                            </td>
                          </tr>,
                        )
                      }
                    }
                  }
                }

                if (storeKey !== StoreName && storeKey !== ExistingStock && storeKey !== AvailableStock) {
                  const currentFilterInfo = quantitiesFilters.find(element => element.value === storeKey)
                  const currentFilterStatus = currentFilterInfo ? currentFilterInfo.status : ''
                  const isCurrentFilterVisible = currentFilterInfo ? currentFilterInfo.visible : ''
                  const totalColumnValues = currentStoreRowSizesValues.find(x => x.name === Total)

                  //remaining filter rows
                  if (isCurrentFilterVisible) {
                    if (
                      totalColumnValues &&
                      (totalColumnValues.quantity > 0 ||
                        (totalColumnValues.quantity < 0 && storeKey === ReturnCustomer))
                    ) {
                      storeKeyRows.push(
                        <tr key={'row-' + keyToSet}>
                          <td colSpan={2} className={storeKey.toLowerCase() + '-cell filter-name-cell'}>
                            <span className={'pretext-block quantities-type-box ' + currentFilterStatus}></span>
                            {renderTranslation(
                              language,
                              TranslationKeys[QuantitiesLabelLangClass + setCapitalLetter(storeKey)],
                            )}
                          </td>
                          {
                            <td>
                              <QuantitiesDynamicCell
                                storeId={store.storeId}
                                storeActiveSizes={currentStoreRowSizesValues}
                                rowKey={storeKey}
                                columnKey={Total}
                                sizeId={0}
                                variantId={variant.variantId}
                              />
                            </td>
                          }
                          {renderSizeCells(store, variant, ExtraRecapRow, keyToSet, currentStoreRowSizesValues, '')}
                        </tr>,
                      )
                    }
                  }
                }
              })

              if (hasStoreStockOrAvilabilityValue && storeIndex < storesFiltered.length - 1) {
                storeKeyRows.push(
                  <tr key={'divider-' + store.storeId}>
                    <td className='special-divider-row' colSpan={sizesAvailable.length + 3}></td>
                  </tr>,
                )
              }
            })
          } else {
            if (isConfigurationFormGruccia && !enableAllVariant && unlockHandling) {
              //when is gruccia and movementation is enable, is always possibile to move elements for local store
              const stockCellCssClass = 'stock-cell bold'
              const rowSpan = unlockHandling ? (!enableAllVariant ? 2 : 1) : 1

              storeKeyRows.push(
                <tr key='avail-row'>
                  <td rowSpan={rowSpan}>{variant.localStoreName}</td>
                  <td className={stockCellCssClass}>
                    {renderTranslation(language, TranslationKeys.acronymAvailability)}
                  </td>
                  {
                    <td className={''}>
                      <QuantitiesDynamicCell
                        storeId={currentStoreOption[0]}
                        storeActiveSizes={variant.sizes}
                        rowKey={AvailableStock}
                        columnKey={Total}
                        sizeId={0}
                        variantId={variant.variantId}
                        allValueSelected={false}
                      />
                    </td>
                  }
                  {sizesAvailable.map(size => {
                    const keyValue = size.sizeId
                    return (
                      <td key={'size-' + keyValue}>
                        <QuantitiesDynamicCell
                          storeId={currentStoreOption[0]}
                          storeActiveSizes={variant.sizes}
                          rowKey={AvailableStock}
                          columnKey={size.name}
                          sizeId={size.sizeId}
                          variantId={variant.variantId}
                        />
                      </td>
                    )
                  })}
                </tr>,
              )

              storeKeyRows.push(
                <tr key='total-row'>
                  <td className='total-cell'></td>
                  {
                    <td>
                      <QuantitiesDynamicCell
                        storeId={currentStoreOption[0]}
                        storeActiveSizes={variant.sizes}
                        rowKey={ExtraRecapRow}
                        columnKey={Total}
                        sizeId={0}
                        variantId={variant.variantId}
                      />
                    </td>
                  }
                  {sizesAvailable.map((size, index) => {
                    const keyValue = size.sizeId
                    return (
                      <td key={'total-' + keyValue}>
                        <QuantitiesDynamicCell
                          storeId={currentStoreOption[0]}
                          storeActiveSizes={variant.sizes}
                          rowKey={ExtraRecapRow}
                          columnKey={size.name}
                          sizeId={size.sizeId}
                          variantId={variant.variantId}
                        />
                      </td>
                    )
                  })}
                </tr>,
              )

              const hasHandling =
                handledQuantitySelection.filter(handled => handled.storeId === currentStoreOption[0]).length !== 0
              if (hasHandling) {
                storeKeyRows.push(
                  <tr key='button-row'>
                    <td colSpan={columnsWithSizes.length} className='quantities-button-row'>
                      <Button
                        className={
                          'btn standard-btn' +
                          (handledQuantitySelection.filter(handled => handled.storeId === currentStoreOption[0])
                            .length !== 0 && unlockHandling
                            ? ''
                            : ' d-none')
                        }
                        onClick={() => {
                          sendQuantitySelection(storesLength)
                        }}
                        size='large'
                        block
                      >
                        <SwapOutlined /> {renderTranslation(language, TranslationKeys.quantitiesHandledButtonLabel)}
                      </Button>
                    </td>
                  </tr>,
                )
              }
            } else {
              renderNoStoreRows(storeKeyRows, variant)
            }
          }
        }

        return storeKeyRows
      })
    } else {
      const emptyResultPlaceholder = []
      const stockCellCssClass = 'stock-cell bold'
      const rowSpan = unlockHandling ? (!enableAllVariant ? 2 : 1) : 1

      emptyResultPlaceholder.push(
        <tr key='empy-existing-row'>
          <td rowSpan={rowSpan}>{renderTranslation(language, TranslationKeys.tableNoResults)}</td>
          <td className={stockCellCssClass}>{renderTranslation(language, TranslationKeys.acronymExistingStock)}</td>
          {
            <td className={''}>
              <QuantitiesDynamicCell
                storeId={''}
                storeActiveSizes={[]}
                rowKey={ExistingStock}
                columnKey={Total}
                sizeId={0}
                variantId={0}
                allValueSelected={false}
              />
            </td>
          }
        </tr>,
      )

      return emptyResultPlaceholder
    }
  }, [
    columnsWithSizes.length,
    currentStoreOption,
    enableAllVariant,
    filterStoresBySearchedValue,
    handledQuantitySelection,
    isConfigurationFormGruccia,
    language,
    quantitiesFilters,
    quantitiesVariant,
    renderNoStoreRows,
    renderSituationTableBoby,
    renderSizeCells,
    sendQuantitySelection,
    situationFilter,
    sizesAvailable,
    unlockHandling,
    updateArticlesBasedOnTableModel,
  ])

  const renderHeader = useCallback(
    isSticky => (
      <thead className={'quantities-table-head' + (isSticky ? ' sticky' : '')}>
        <tr>
          {columnsWithSizes.map(column => {
            let columnsClass = 'table-col'

            if (column.key === PlaceHolderCol) {
              columnsClass += ' placeholder-col'
            }

            if (column.key === Total) {
              columnsClass += ' total-col'
            }

            if (column.key !== PlaceHolderCol && column.key !== StoreName && column.key !== Total) {
              columnsClass += ' sizes'
            }

            if (column.key === StoreName) {
              if (!enableAllVariant && situationFilter && !unlockHandling) {
                return (
                  <th key={column.key} className={columnsClass + ' situation'}>
                    <span id='quantities-input-container' className='quantities-input-container situation-enable'>
                      {renderTranslation(language, TranslationKeys.situationTableHeadLabel)}
                    </span>
                  </th>
                )
              }
              return (
                <th key={column.key} className={columnsClass}>
                  <span className='quantities-input-container'>
                    <Input
                      variant={'borderless'}
                      placeholder={renderTranslation(
                        language,
                        TranslationKeys[QuantitiesHeadLangClass + setCapitalLetter(column.key)],
                      )}
                      addonBefore={<SearchOutlined className='input-search-icon' />}
                      className={'quantities-search' + (searchValue !== '' ? ' searched' : '')}
                      value={searchValue}
                      onChange={e => {
                        setSearchValue(e.target.value)
                      }}
                      onClick={stopPropagation}
                      allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
                    />
                  </span>
                </th>
              )
            }
            return (
              <th
                key={column.key}
                className={columnsClass}
                style={{ width: column.isSize ? calculateColumnWidthBasedOnText(column.title) : 'inherit' }}
              >
                {column.key !== PlaceHolderCol
                  ? column.title
                    ? column.title
                    : renderTranslation(
                        language,
                        TranslationKeys[QuantitiesHeadLangClass + setCapitalLetter(column.key)],
                      )
                  : null}
              </th>
            )
          })}
        </tr>
      </thead>
    ),
    [columnsWithSizes, enableAllVariant, language, searchValue, situationFilter, unlockHandling],
  )

  return (
    <>
      <div className='quantity-container' ref={tableRef}>
        <table className='quantities-table'>
          {renderHeader(isHeadSticky)}
          <tbody className='quantities-table-body'>{tableBodyRows}</tbody>
        </table>
      </div>
    </>
  )
}

export default Quantities
